import { v4 as uuidv4 } from 'uuid';
import {
  dispatch,
  getAllChannelsAndThreads,
  getUserId,
  getWaConfigId,
  getWorkspace,
  getWorkspaceId,
  PublishMessagePayload,
  PublishToBotRes,
  store,
  updateScrollToBottom,
  upsertMessage,
  useAppSelector,
  usePublishMessageMutation,
  usePublishToBotMutation,
  useSendWhatsappMessageMutation,
} from '@zonofi/seller-rtk-store';

interface PublishWhatsapp {
  name: string;
  customerPhone: string;
  text?: string;
  type?: string;
  intent?: string;
  reaction?: string;
  replyText?: string;
  contextId?: string;
  passContextId?: boolean;
  _id?: string;
}

type PublishMessage = Omit<PublishMessagePayload, 'sellerWorkspaceId'>;

export const usePublishMessage = () => {
  const [publish] = usePublishMessageMutation();
  const [publishToBot, publishToBotRes] = usePublishToBotMutation();
  const [sendWhatsappMessage] = useSendWhatsappMessageMutation();
  const uuid = getUserId(store.getState());
  const allChannels = getAllChannelsAndThreads(store.getState());
  const workspaceId = getWorkspaceId(store.getState());
  const workspace = getWorkspace(store.getState());
  const waConfigId = useAppSelector(getWaConfigId);

  const publishMessage = async ({
    channelId: channel = '',
    message,
    inviteIds = [],
    isInternalNote = false,
    conversationId,
  }: PublishMessage) => {
    const currentChannel = allChannels[channel];
    const { sellerWorkspaceId } = currentChannel || {};
    try {
      const payload: PublishMessagePayload = {
        sellerWorkspaceId: sellerWorkspaceId || workspaceId,
        message: { ...message, source: 'system' },
      };
      if (inviteIds?.length) payload.inviteIds = inviteIds;
      else payload.channelId = channel;
      if (conversationId) payload.conversationId = conversationId;
      if (isInternalNote) payload.isInternalNote = isInternalNote;
      if (waConfigId) {
        payload.message.waConfigId = waConfigId;
      }
      await publish(payload)
        .unwrap()
        .then(() => {
          dispatch(updateScrollToBottom(true));
          if (isInternalNote && !conversationId) {
            dispatch(
              upsertMessage({
                channel,
                message,
                timetoken: Date.now() + '0000',
                publisher: uuid,
                isInternalNote: true,
              })
            );
          }
        });
    } catch (error) {
      console.error(error);
    }
  };

  const publishBotMessage = async (
    sellerWorkspaceId: string,
    message: any,
    onSuccess?: (params?: PublishToBotRes) => void,
    onError?: (error?: any) => void
  ) => {
    const activity = {
      ...message,
      sellerWorkspaceId: sellerWorkspaceId,
    };

    publishToBot({
      body: { activity },
    })
      .unwrap()
      .then((res) => {
        onSuccess && onSuccess(res);
        dispatch(updateScrollToBottom(true));
      })
      .catch((err) => {
        console.log(err);
        onError && onError(err);
      });
  };

  const publishWhatsappMessage = async ({
    name,
    customerPhone,
    text,
    type = 'text',
    intent,
    reaction,
    replyText,
    contextId,
    passContextId = true,
    _id = `campaign-jtbd-${uuidv4()}`,
  }: PublishWhatsapp) => {
    sendWhatsappMessage({
      params: {
        sellerWorkspaceId: workspaceId,
      },
      body: {
        object: 'whatsapp_business_account',
        entry: [
          {
            id: '518047538061874',
            changes: [
              {
                field: 'messages',
                value: {
                  messaging_product: 'whatsapp',
                  metadata: {
                    display_phone_number: workspace?.user?.mobile ?? '',
                    phone_number_id: '482675131604344',
                    workspace_id: workspaceId,
                  },
                  contacts: [
                    {
                      profile: { name },
                      wa_id: customerPhone,
                    },
                  ],
                  messages: [
                    {
                      from: customerPhone,
                      id: _id,
                      timestamp: `${Math.floor(Date.now() / 1000)}`,
                      type: type,
                      ...(contextId
                        ? {
                            context: {
                              ...(passContextId ? { id: contextId } : {}),
                              ...(type === 'reaction'
                                ? { text: replyText }
                                : {}),
                            },
                          }
                        : {}),
                      ...(text ? { text: { body: text } } : {}),
                      ...(type === 'reaction'
                        ? {
                            reaction: {
                              message_id: contextId,
                              emoji: reaction,
                            },
                          }
                        : {}),
                      ...(intent ? { intent: intent } : {}),
                    },
                  ],
                },
              },
            ],
          },
        ],
      },
    });
  };

  return {
    publishMessage,
    publishBotMessage,
    publishToBotRes,
    publishWhatsappMessage,
  };
};
